/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { isRouteErrorResponse } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SeoMeta } from '@/components/seo-meta';

/**
 * Inner content for a route error (404 / 403 / 500 / generic) — the status number, heading,
 * messages, and a "back to homepage" CTA. It renders **no chrome** (no header/footer) so the
 * caller controls the surrounding layout and width: the `_app` ErrorBoundary wraps it in the
 * storefront shell + `section-container` (UCR-2787), while the root ErrorBoundary keeps its own
 * standalone wrapper for errors thrown outside the app layout.
 *
 * It also owns the document title for error pages (UCR-2985) — the `_app.$` catch-all never renders
 * (its loader always throws), so without this a 404 tab would show the raw URL instead of
 * `Page not found | {siteName}`. Error pages are `noindex`.
 *
 * Requires an `I18nextProvider` (it reads the `routeError` namespace) — guaranteed in the `_app`
 * subtree. The CTA is a plain `<a href="/">` (full reload) so navigating home also clears the
 * error state.
 */
export function RouteErrorContent({ error }: { error: unknown }): ReactElement {
    const { t } = useTranslation('routeError');
    const status = isRouteErrorResponse(error) ? error.status : undefined;

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;
    let statusDetails: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
        statusDetails = t('404.details');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        // NOTE: this project's design-system theme remaps Tailwind's `max-w-2xl`/`max-w-3xl` to the
        // small spacing scale (~tens of px), so they can't be used as a content width — use an
        // explicit rem cap. The outer `section-container` (applied by the caller) sets the page width.
        <div className="flex min-h-[50vh] items-center justify-center py-12">
            <SeoMeta title={title} noIndex />
            <div className="mx-auto w-full max-w-[42rem] text-center">
                {status && <div className="text-error-status font-bold leading-none mb-8">{status}</div>}
                <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>
                {message && <p className="text-lg text-muted-foreground mb-4">{message}</p>}
                {secondaryMessage && <p className="text-base text-muted-foreground mb-4">{secondaryMessage}</p>}
                {statusDetails && <p className="text-sm text-muted-foreground mb-12 opacity-50">{statusDetails}</p>}
                <div>
                    <a
                        href="/"
                        className="inline-block rounded-none bg-primary px-12 py-3 text-base font-semibold text-primary-foreground no-underline transition-colors hover:bg-primary/90">
                        {t('goToHomepage')}
                    </a>
                </div>
            </div>
        </div>
    );
}
